import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import SplashScreen from "@/components/SplashScreen";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import PaymentPage from "@/pages/PaymentPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import FeedPage from "@/pages/FeedPage";
import SalasPage from "@/pages/SalasPage";
import VocesPage from "@/pages/VocesPage";
import JoinRoomPage from "@/pages/JoinRoomPage";
import RoomChatPage from "@/pages/RoomChatPage";
import EventsPage from "@/pages/EventsPage";
import IndividualSessionPage from "@/pages/IndividualSessionPage";
import ProfilePage from "@/pages/ProfilePage";
import AdminPage from "@/pages/AdminPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import PlatformHealthPage from "@/pages/PlatformHealthPage";
import NotificationsPage from "@/pages/NotificationsPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import { useKeyboardOpen } from "@/hooks/useKeyboardOpen";
import BottomNav from "@/components/BottomNav";
import TopHeader from "@/components/TopHeader";
import InstallPWAPrompt from "@/components/InstallPWAPrompt";

function ProtectedRoute({ requirePayment = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5EEDB]">
        <div className="animate-pulse text-[#5B3E2E] font-heading text-2xl">Refugium</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (user.email_verified === false) return <Navigate to="/verify-email" replace />;
  if (requirePayment && !user.has_paid) return <Navigate to="/payment" replace />;
  return <Outlet />;
}

function AppLayout() {
  const keyboardOpen = useKeyboardOpen();
  return (
    <div className="h-[100dvh] flex flex-col bg-[#F5EEDB] overflow-hidden">
      <TopHeader />
      <main className={`flex-1 min-h-0 pt-[calc(3.5rem+env(safe-area-inset-top))] ${keyboardOpen ? "pb-0" : "pb-[calc(5rem+env(safe-area-inset-bottom))]"} overflow-y-auto`}>
        <Outlet />
      </main>
      <div className={keyboardOpen ? "hidden" : ""} data-testid="bottom-nav-wrapper">
        <BottomNav />
      </div>
    </div>
  );
}

function PublicOnly() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5EEDB]">
        <div className="animate-pulse text-[#5B3E2E] font-heading text-2xl">Refugium</div>
      </div>
    );
  }
  if (user && user.email_verified === false) return <Navigate to="/verify-email" replace />;
  if (user && user.has_paid) return <Navigate to="/app/feed" replace />;
  if (user && !user.has_paid) return <Navigate to="/payment" replace />;
  return <Outlet />;
}

function App() {
  const [showSplash, setShowSplash] = useState(true);  // se muestra en cada montaje

  useEffect(() => {
    // Mostrar splash cuando la app vuelve al foco tras >60s inactiva
    // (usuario cerró/cambió de app, minimizó, bloqueó pantalla…)
    const INACTIVITY_MS = 10 * 60 * 1000;  // 10 minutos
    let lastHiddenAt = null;

    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        lastHiddenAt = Date.now();
      } else if (document.visibilityState === "visible") {
        if (lastHiddenAt && Date.now() - lastHiddenAt >= INACTIVITY_MS) {
          setShowSplash(true);
        }
        lastHiddenAt = null;
      }
    };

    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  return (
    <BrowserRouter>
      {showSplash && <SplashScreen onDone={() => setShowSplash(false)} />}
      <AuthProvider>
        <Routes>
          <Route element={<PublicOnly />}>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
          </Route>
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route element={<ProtectedRoute />}>
            <Route path="/payment" element={<PaymentPage />} />
            <Route path="/payment/success" element={<PaymentSuccessPage />} />
          </Route>
          <Route element={<ProtectedRoute requirePayment />}>
            <Route path="/app" element={<AppLayout />}>
              <Route index element={<Navigate to="/app/feed" replace />} />
              <Route path="feed" element={<FeedPage />} />
              <Route path="salas" element={<SalasPage />} />
              <Route path="voces" element={<VocesPage />} />
              <Route path="salas/join" element={<JoinRoomPage />} />
              <Route path="salas/:id" element={<RoomChatPage />} />
              <Route path="events" element={<EventsPage />} />
              <Route path="sessions" element={<IndividualSessionPage />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="admin" element={<AdminPage />} />
              <Route path="admin/dashboard" element={<AdminDashboardPage />} />
              <Route path="admin/estado" element={<PlatformHealthPage />} />
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-center" richColors />
        <InstallPWAPrompt />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
